// Service page content for Parent visas.
// Sourced from docs/research/australian-visa-pathways.md §3.
// COMPLIANCE: we gather and organise documents and coordinate the matter; a
// registered migration agent confirms the balance-of-family test, advises on
// the visa, and lodges. Brutally honest about queues and cost (qualitative, no
// unverified numbers stated as fact); no guarantees.

const parentVisa = {
  groupSlug: "family-partner",
  slug: "parent-visa",
  title: "Parent Visa",
  eyebrow: "Family & Partner",
  intro:
    "Parent visas let the parents of a settled Australian child live in Australia. They come in two broad types: non-contributory visas that cost little but carry extremely long waits, and contributory visas that are far faster but very expensive. Choosing the right one starts with an honest conversation about time and cost.",

  chips: [
    { label: "Visa type", value: "Temporary or permanent options" },
    { label: "Sponsor", value: "A settled Australian child" },
    { label: "Key test", value: "Balance-of-family test" },
    { label: "Trade-off", value: "Cost versus waiting time" },
  ],

  whoFor: [
    "Parents of a child who is a settled Australian citizen, permanent resident, or eligible New Zealand citizen.",
    "Families weighing the cost of a contributory visa against the wait of a non-contributory one.",
    "People who want the real cost and timing laid out clearly, and a registered migration agent to advise, before committing money or years.",
  ],

  essentials: [
    {
      title: "The balance-of-family test",
      detail:
        "At least half of your children, or more children than in any other single country, must live permanently in Australia.",
    },
    {
      title: "An approved sponsor",
      detail:
        "Usually your settled Australian child sponsors you and must meet the sponsorship requirements.",
    },
    {
      title: "Contributory or non-contributory",
      detail:
        "Contributory parent visas are much faster but carry large second-instalment charges. Non-contributory visas cost far less but can involve waits measured in decades.",
    },
    {
      title: "Temporary or permanent routes",
      detail:
        "Some families use a temporary contributory option to spread the cost, then move to the permanent stage.",
    },
    {
      title: "Annual caps apply",
      detail:
        "The parent program is capped each year, which lengthens queues. This is central to planning realistically.",
    },
    {
      title: "Health and character",
      detail:
        "Parents must meet health and character requirements, and health can be a significant factor for older applicants.",
    },
  ],

  process: [
    {
      title: "Lay out the facts",
      detail:
        "We set out the real cost and waiting time of each parent visa, and a registered migration agent advises on which one fits your situation.",
    },
    {
      title: "Check the balance-of-family test",
      detail:
        "A registered migration agent confirms whether you meet the test before anything else, since it gates every parent visa.",
    },
    {
      title: "Plan the timing and budget",
      detail:
        "We help you organise around the charges, instalments, and queues for the option you choose.",
    },
    {
      title: "Prepare and lodge",
      detail:
        "We prepare and organise a thorough file with the sponsor documents, and a registered migration agent lodges it and deals with the Department.",
    },
    {
      title: "Support through the wait",
      detail:
        "We keep your file current and coordinated as your queue position progresses, and a registered migration agent advises as things change.",
    },
  ],

  faqs: [
    {
      q: "How long do parent visas take?",
      a: "It varies enormously. Non-contributory parent visas can involve waits so long that, honestly, they are not practical for many families. Contributory parent visas are much faster but still multi-year. A registered migration agent gives you the current, realistic position before you commit.",
    },
    {
      q: "Why are contributory parent visas so expensive?",
      a: "They carry a large second instalment of the Visa Application Charge, payable before grant, which can run into the tens of thousands per parent. The exact figure is set by the government and changes, so a registered migration agent confirms it for your case rather than quoting an outdated number.",
    },
    {
      q: "What is the balance-of-family test?",
      a: "It is the threshold question for parent visas: broadly, at least half your children must live permanently in Australia, or more of them live here than in any other country. If you do not meet it, a parent visa is not available, so a registered migration agent checks this first.",
    },
    {
      q: "Is there a faster way to bring my parents over?",
      a: "Some families use a temporary contributory option to spread cost and arrive sooner, and visitor visas can help in the meantime. A registered migration agent talks through every realistic option for your circumstances.",
    },
  ],

  related: ["partner-visa", "child-visa", "prospective-marriage-300"],

  lastVerified: "13 July 2026",
};

export default parentVisa;
